import { useSearchParams, Navigate } from "react-router-dom";
import { useAuthStore } from "@/lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { BooleanIcon } from "@nodus/ui";
import { Card, Button, Text, Title, Stack, Flex, Box, Group } from "@mantine/core";
import { Link2, X, Unlink } from "lucide-react";
import type { AxiosError } from "axios";

const PLATFORM_LABELS: Record<string, string> = {
  slack: "Slack",
  whatsapp: "WhatsApp",
  web: "Web",
};

interface LinkData {
  platform: string;
  platform_id: string;
  user_name: string;
  email: string;
}

export default function LinkPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuthStore();

  const token = searchParams.get("token") || "";
  const platform = searchParams.get("platform") || "";
  const platformId = searchParams.get("platform_id") || "";
  const callbackUrl = searchParams.get("callback_url") || "";

  // Check if already linked (404 = not linked, which is fine)
  const { data: existingLink, isLoading: checkingLink } = useQuery({
    queryKey: ["platform-link", platform, platformId],
    queryFn: async () => {
      try {
        const res = await api.get("/tenant-portal/platform-link/", {
          params: { platform, platform_id: platformId },
        });
        return res.data as LinkData;
      } catch (err) {
        const status = (err as AxiosError)?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    enabled: isAuthenticated && !!platform && !!platformId,
    retry: false,
  });

  const linkMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post("/tenant-portal/platform-link/", {
        platform,
        platform_id: platformId,
        token,
        callback_url: callbackUrl,
      });
      return res.data as LinkData;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["platform-link", platform, platformId] }),
  });

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      await api.delete("/tenant-portal/platform-link/", {
        params: { platform, platform_id: platformId },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["platform-link", platform, platformId] }),
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: `/link?${searchParams.toString()}` }} replace />;
  }

  if (!platform || !platformId) {
    return (
      <Flex align="center" justify="center" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
        <Card w="100%" maw={448} withBorder padding="xl">
          <Stack align="center" gap="md" pt="md">
            <X size={48} style={{ color: "var(--mantine-color-red-6)" }} />
            <Text c="red" fw={500}>
              Invalid link. Please use /link in {PLATFORM_LABELS[platform] || "your chat app"} to get a new one.
            </Text>
          </Stack>
        </Card>
      </Flex>
    );
  }

  if (checkingLink) {
    return (
      <Flex align="center" justify="center" mih="100vh">
        <Text c="dimmed">Loading...</Text>
      </Flex>
    );
  }

  // Success state after linking
  if (linkMutation.isSuccess) {
    const data = linkMutation.data;
    return (
      <Flex align="center" justify="center" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
        <Card w="100%" maw={448} withBorder padding="xl">
          <Stack align="center" gap="sm" ta="center">
            <Box
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--mantine-color-green-1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BooleanIcon value={true} size={24} />
            </Box>
            <Title order={3}>Account Linked</Title>
            <Text c="dimmed" fz="sm">
              <strong>{data.user_name}</strong> ({data.email}) is now linked to your{" "}
              {PLATFORM_LABELS[platform] || platform} account. You can close this window.
            </Text>
          </Stack>
        </Card>
      </Flex>
    );
  }

  // Success state after unlinking
  if (unlinkMutation.isSuccess) {
    return (
      <Flex align="center" justify="center" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
        <Card w="100%" maw={448} withBorder padding="xl">
          <Stack align="center" gap="sm" ta="center">
            <Box
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--mantine-color-default-hover)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Unlink size={24} style={{ color: "var(--mantine-color-dimmed)" }} />
            </Box>
            <Title order={3}>Account Unlinked</Title>
            <Text c="dimmed" fz="sm">
              Your {PLATFORM_LABELS[platform] || platform} account has been unlinked. You can close this window or use
              /link to link a different account.
            </Text>
          </Stack>
        </Card>
      </Flex>
    );
  }

  // Already linked -- show status + unlink option
  if (existingLink) {
    return (
      <Flex align="center" justify="center" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
        <Card w="100%" maw={448} withBorder padding="xl">
          <Stack align="center" gap="sm" ta="center" mb="md">
            <Box
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--mantine-color-green-1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BooleanIcon value={true} size={24} />
            </Box>
            <Title order={3}>Already Linked</Title>
            <Text c="dimmed" fz="sm">
              Your {PLATFORM_LABELS[platform] || platform} account is linked as{" "}
              <strong>{existingLink.user_name}</strong> ({existingLink.email}).
            </Text>
          </Stack>
          <Group grow>
            <Button
              variant="outline"
              onClick={() => window.close()}
            >
              Close
            </Button>
            <Button
              color="red"
              onClick={() => unlinkMutation.mutate()}
              disabled={unlinkMutation.isPending}
            >
              {unlinkMutation.isPending ? "Unlinking..." : "Unlink Account"}
            </Button>
          </Group>
        </Card>
      </Flex>
    );
  }

  // Not linked -- show link form
  const errorMessage = linkMutation.error
    ? (linkMutation.error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
      "Linking failed. Please try again."
    : null;

  return (
    <Flex align="center" justify="center" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
      <Card w="100%" maw={448} withBorder padding="xl">
        <Stack align="center" gap="sm" ta="center" mb="md">
          <Box
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "var(--mantine-primary-color-light)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Link2 size={24} style={{ color: "var(--mantine-primary-color-filled)" }} />
          </Box>
          <Title order={3}>Link Your Account</Title>
          <Text c="dimmed" fz="sm">
            Connect your {PLATFORM_LABELS[platform] || platform} account to your identity here. This allows the AI
            assistant to know who you are when you send messages.
          </Text>
        </Stack>
        <Stack gap="md">
          {user && (
            <Box p={12} style={{ borderRadius: 8, border: "1px solid var(--mantine-color-default-border)" }}>
              <Text fw={500} fz="sm">Signed in as:</Text>
              <Text c="dimmed" fz="sm">
                {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email}
              </Text>
            </Box>
          )}
          {errorMessage && <Text fz="sm" c="red" ta="center">{errorMessage}</Text>}
        </Stack>
        <Group grow mt="md">
          <Button variant="outline" onClick={() => window.close()} disabled={linkMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => linkMutation.mutate()} disabled={linkMutation.isPending} loading={linkMutation.isPending}>
            Link Account
          </Button>
        </Group>
      </Card>
    </Flex>
  );
}
